import React from 'react';

import { Alert, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import type { CompetitorProperty } from '~/types/services/priceParrot';

import { useCompetitorOfferOptions } from './useCompetitorOfferOptions';
import {
  cancellationText,
  formatAmountWithAud,
  formatDate,
  formatStayPrice,
  plural,
  stayBasis,
  stayOf,
  taxBasisLabel,
} from './utils';

interface Props {
  property: CompetitorProperty;
  /** A past collection to show instead of the current deal. */
  collectionId?: string;
}

export default function CompetitorOfferOptionsTable({ property, collectionId }: Props) {
  const { options, isLoading, error } = useCompetitorOfferOptions(property, collectionId);

  if (isLoading) {
    return <CircularProgress size={24} />;
  }
  if (error) {
    return <Alert severity="error">Failed to load room options: {error.message}</Alert>;
  }
  if (!options.length) {
    return <Typography color="text.secondary">No room options on this deal.</Typography>;
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Option</TableCell>
          <TableCell>Per night</TableCell>
          <TableCell>Stay price</TableCell>
          <TableCell>Min stay</TableCell>
          <TableCell>Occupancy</TableCell>
          <TableCell>Cancellation</TableCell>
          <TableCell>Priced nights</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {options.map((option) => (
          <TableRow key={option.id}>
            <TableCell sx={{ maxWidth: 320 }}>
              <Typography variant="body2" fontWeight="medium">
                {option.name ?? option.id}
              </Typography>
              {option.inclusions && (
                <Typography variant="caption" color="text.secondary" component="div">
                  {option.inclusions}
                </Typography>
              )}
            </TableCell>
            <TableCell>
              <Typography variant="body2">
                {formatAmountWithAud(option.pricePerNight, option.pricePerNightCurrency, option.pricePerNightAud)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {taxBasisLabel(option.pricePerNightIncludesTax)}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">{formatStayPrice(stayOf(option))}</Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                {stayBasis(option.stayNights, option.adults, option.stayCheckIn)}
                {option.stayPriceQuoted ? ' · competitor quote' : ''}
              </Typography>
            </TableCell>
            <TableCell>{option.minNights ? plural(option.minNights, 'night') : '—'}</TableCell>
            <TableCell>
              {option.adults ? plural(option.adults, 'adult') : '—'}
              {option.maxAdults ? ` (max ${option.maxAdults})` : ''}
              {option.maxGuests ? ` (max ${plural(option.maxGuests, 'guest')})` : ''}
            </TableCell>
            <TableCell sx={{ maxWidth: 240 }}>{cancellationText(option)}</TableCell>
            <TableCell>
              {option.firstAvailableNight
                ? `${formatDate(option.firstAvailableNight)} – ${formatDate(option.lastAvailableNight)}`
                : '—'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
